import type { McpPolicyIntentGroup } from './policyTypes';

export const MCP_POLICY_STATUS = {
  idle: 'idle',
  loading: 'loading',
  success: 'success',
  error: 'error',
} as const;

export type McpPolicyStatus =
  (typeof MCP_POLICY_STATUS)[keyof typeof MCP_POLICY_STATUS];

/** Why policy check failed; only set when `mcpPolicyStatus === 'error'`. */
export const MCP_POLICY_ERROR_KIND = {
  policyFetchFailed: 'policyFetchFailed',
  redirectNotAllowed: 'redirectNotAllowed',
  missingRedirect: 'missingRedirect',
  workspaceAccessRevoked: 'workspaceAccessRevoked',
  unexpected: 'unexpected',
} as const;

export type McpPolicyErrorKind =
  (typeof MCP_POLICY_ERROR_KIND)[keyof typeof MCP_POLICY_ERROR_KIND];

/** One of the independent MCP policy checks. */
export type McpPolicySource = 'site' | 'trello' | 'bitbucket';

export type PolicyState =
  | { status: 'idle' }
  | {
      status: 'loading';
      /** Selection this request belongs to. Set by production policy requests. */
      requestKey?: string;
    }
  | {
      status: 'error';
      /** Selection this result belongs to. Set by production policy requests. */
      requestKey?: string;
      errorMessage: string | null;
      errorKind: McpPolicyErrorKind;
      errorWorkspaceName?: string | null;
    }
  | {
      status: 'success';
      /** Selection this result belongs to. Set by production policy requests. */
      requestKey?: string;
      byIntent: McpPolicyIntentGroup[];
      crossOrgScopesRemoved: boolean;
    };

/** The independent policy-check states, keyed by source. */
export type McpPolicies = Readonly<Record<McpPolicySource, PolicyState>>;

/** No policy check has run yet. Also the baseline for test fixtures. */
export const IDLE_MCP_POLICIES: McpPolicies = {
  site: { status: 'idle' },
  trello: { status: 'idle' },
  bitbucket: { status: 'idle' },
};

/**
 * The checks that speak for the current flow, in reporting precedence.
 *
 * A Trello-only flow still runs the site check — it auto-selects a hidden
 * workspace purely to trigger the fetch — but the site selector isn't rendered,
 * so a site result there can be neither reported nor acted on. Only Trello
 * speaks for those flows; every caller must scope to this list so a check that
 * cannot surface an error also cannot block the user.
 *
 * Dropping Bitbucket there is safe because this list is also what decides
 * whether Bitbucket is offered: BitbucketWorkspaceSelector renders nothing
 * unless 'bitbucket' is in it, and McpFlowParameters will not post an ARI
 * unless 'bitbucket' is in it. So a Trello-only flow has no Bitbucket field to
 * pick from, no selection to check, and no target to submit — the check stays
 * idle with nothing to contribute.
 *
 * That is a property of this list, not of the scopes. Bitbucket participation
 * comes from `/consent/info`'s workspace list alone (see
 * useBitbucketWorkspaces, which reads no scopes), so a Trello-only token can
 * perfectly well arrive with Bitbucket workspaces available; without the render
 * gate the field would show a real, selectable target whose permissions this
 * list excludes from the screen and whose failures nothing could report.
 *
 * If Bitbucket ever has to be offered alongside Trello, add it back here
 * first — the field, the Accept gate and the submitted params all follow this
 * list, so a workspace the user can select and grant would otherwise have its
 * toolsets hidden.
 */
export function getOrderedPolicySources(scopeClassification: {
  hasTrello: boolean;
  hasAtlassian: boolean;
}): readonly McpPolicySource[] {
  return scopeClassification.hasTrello && !scopeClassification.hasAtlassian
    ? ['trello']
    : ['site', 'trello', 'bitbucket'];
}

/** Aggregate independent checks while any in-flight result is still pending. */
export function getCombinedPolicyStatus(
  policies: McpPolicies,
  orderedSources: readonly McpPolicySource[],
): McpPolicyStatus {
  const statuses = orderedSources.map((source) => policies[source].status);
  if (statuses.includes('loading')) return 'loading';
  if (statuses.includes('error')) return 'error';
  if (statuses.includes('success')) return 'success';
  return 'idle';
}

/**
 * Pick the error represented by the context's combined error fields.
 *
 * Every failure is presented the same way — McpPolicyAlerts takes the panel
 * over — so the caller's stable source order decides which one is reported when
 * several checks fail at once.
 */
export function getPrimaryPolicyErrorSource(
  policies: McpPolicies,
  orderedSources: readonly McpPolicySource[],
): McpPolicySource | null {
  return (
    orderedSources.find((source) => policies[source].status === 'error') ?? null
  );
}

/**
 * Whether a check completed and granted nothing.
 *
 * `{"success": true, "toolsets": {"byIntent": [], ...}}` — the selection is
 * valid but grants nothing, which is worth saying rather than leaving Accept
 * inert with no explanation. A *failed* check is not this: it is presented
 * full-panel by McpPolicyAlerts, since the selection isn't necessarily at fault.
 */
export function grantedNoPermissions(policy: PolicyState): boolean {
  return policy.status === 'success' && policy.byIntent.length === 0;
}

/**
 * Whether a panel-level "permissions are blocked" empty state is still needed.
 *
 * Only ask this when the merged toolset list is empty — at that point every
 * check that succeeded returned nothing. The site and Bitbucket selectors
 * already say so in their own message slot, so a panel-wide empty state just
 * repeats it. The Trello selector has no inline message, so a Trello result is
 * the only one that still needs the panel to speak.
 */
export function needsNoPermissionsEmptyState(policies: McpPolicies): boolean {
  return grantedNoPermissions(policies.trello);
}

/**
 * Whether any check that speaks for this flow failed.
 *
 * A failed check blocks Accept regardless of which source it came from: the
 * permissions that selection would have granted are unknown, so consenting
 * would grant something neither we nor the user can see. Each failing selector
 * reports the error inline so the user can change that selection and retry.
 *
 * Scoped to `orderedSources` for the same reason those exist — a check whose
 * error has nowhere to render must not silently disable Accept.
 */
export function hasFailedPolicyCheck(
  policies: McpPolicies,
  orderedSources: readonly McpPolicySource[],
): boolean {
  return orderedSources.some((source) => policies[source].status === 'error');
}

/** Stable identity for every input that can change a policy response. */
export function getPolicyRequestKey(
  contextJwt: string,
  targets: readonly string[],
): string {
  return JSON.stringify([contextJwt, targets]);
}

/**
 * Ignore a result produced for a previous selection.
 *
 * React effects start the next request after render, so without this guard a
 * newly selected target briefly inherits the previous target's success state,
 * toolsets, and enabled Accept button. Production reducer actions always set a
 * request key; keyless states remain supported for lightweight test fixtures.
 */
export function getPolicyStateForRequest(
  policy: PolicyState,
  requestKey: string | null,
): PolicyState {
  if (requestKey == null) return { status: 'idle' };
  if (policy.status === 'idle') return policy;
  return policy.requestKey == null || policy.requestKey === requestKey
    ? policy
    : { status: 'idle' };
}

type PolicyAction =
  | { type: 'RESET' }
  | { type: 'LOADING'; requestKey: string }
  | {
      type: 'SUCCESS';
      requestKey: string;
      byIntent: McpPolicyIntentGroup[];
      crossOrgScopesRemoved: boolean;
    }
  | {
      type: 'ERROR';
      requestKey: string;
      errorMessage: string | null;
      errorKind: McpPolicyErrorKind;
      errorWorkspaceName?: string | null;
    };

export function policyReducer(
  _: PolicyState,
  action: PolicyAction,
): PolicyState {
  switch (action.type) {
    case 'RESET':
      return { status: 'idle' };
    case 'LOADING':
      return { status: 'loading', requestKey: action.requestKey };
    case 'SUCCESS':
      return {
        status: 'success',
        requestKey: action.requestKey,
        byIntent: action.byIntent,
        crossOrgScopesRemoved: action.crossOrgScopesRemoved,
      };
    case 'ERROR':
      return {
        status: 'error',
        requestKey: action.requestKey,
        errorMessage: action.errorMessage,
        errorKind: action.errorKind,
        errorWorkspaceName: action.errorWorkspaceName ?? null,
      };
  }
}
